/**
 * mk-cart-delivery-resync.js
 *
 * Holder line item properties Leveringstid / Forventet på lager / _in_stock_ synkroniseret
 * med produkt-feedet. Snapshottet ved add-to-cart kan blive forældet hvis lagerstatus
 * ændrer sig efter varen er lagt i kurv.
 *
 * Hooks:
 *   window.mkCartDeliveryResync.run()   — kører resync, returnerer Promise
 *   window.mkCartDeliveryResync.debug   — true/false, toggler verbose logging
 */
(function () {
  'use strict';

  // Legacy danske keys + nuværende lokaliserede labels skal alle ryddes op før
  // den nye property tilføjes. Uden legacy-fjernelse ville items lagt i kurv før
  // locale-fixet beholde "Leveringstid" på DE og duplikere row sammen med ny "Lieferzeit".
  var LEGACY_PROPS = ['Leveringstid', 'Forventet på lager', 'Status'];
  function buildDeliveryProps() {
    var labels = (typeof window !== 'undefined' && window.mkDeliveryLabels) || {};
    var localized = [labels.deliveryTime, labels.expectedDate, labels.status].filter(Boolean);
    var combined = LEGACY_PROPS.slice();
    localized.forEach(function (name) {
      if (combined.indexOf(name) === -1) combined.push(name);
    });
    return combined;
  }
  var DELIVERY_PROPS = buildDeliveryProps();
  var IN_STOCK_PROP = '_in_stock_';
  // ID på main-product section i templates/product.json (key i "sections")
  var SECTION_ID = 'main';
  var TAG = '[mk-resync]';

  var inFlight = null;
  var debugMode = false;

  function log() {
    if (!debugMode) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift(TAG);
    console.log.apply(console, args);
  }
  function warn() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift(TAG);
    console.warn.apply(console, args);
  }
  function group(label) {
    if (!debugMode) return;
    console.group(TAG + ' ' + label);
  }
  function groupEnd() {
    if (!debugMode) return;
    console.groupEnd();
  }

  function fetchCart() {
    log('GET /cart.js');
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('cart.js fetch failed: ' + r.status);
      return r.json();
    });
  }

  function fetchDeliveryInfo(handle, variantId) {
    // Vi bruger Shopify Section Rendering API til at hente PRÆCIS det main-product
    // section renderer på PDP. På den måde får vi den vendor-specifikke leveringstid
    // fra block.settings (graphie-stock-block) i stedet for at duplikere logikken
    // i et alt-template — som ikke har adgang til block-konteksten.
    var url = '/products/' + handle + '?variant=' + variantId + '&sections=' + SECTION_ID + '&_t=' + Date.now();
    log('GET', url);
    return fetch(url, {
      credentials: 'same-origin',
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('sections fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (sections) {
        var html = sections && sections[SECTION_ID];
        if (!html || typeof html !== 'string') {
          throw new Error('sections response missing "' + SECTION_ID + '" key — got: ' + Object.keys(sections || {}).join(','));
        }
        log('  ↳ section html length:', html.length);
        return parseDeliveryFromSectionHtml(html);
      });
  }

  function parseDeliveryFromSectionHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var deliveryEl = doc.querySelector('.graphie-delivery');
    var instockEl = doc.querySelector('.graphie-instock');
    var countEl = doc.querySelector('.graphie-inventory-count');

    var property = '';
    var value = '';
    var show = false;

    if (deliveryEl) {
      var span = deliveryEl.querySelector('span');
      var fullText = (deliveryEl.textContent || '').trim();
      var colonIdx = fullText.indexOf(':');
      if (span && colonIdx > -1) {
        property = fullText.slice(0, colonIdx).trim();
        value = (span.textContent || '').trim();
        if (property && value) show = true;
      }
    }

    // In-stock: graphie-instock tilstede AND ikke remote-stock label
    var labels = (typeof window !== 'undefined' && window.mkDeliveryLabels) || {};
    var remoteLabel = (labels.remoteStock || 'fjernlager').toLowerCase();
    var instockText = instockEl ? (instockEl.textContent || '').toLowerCase() : '';
    var inStock = !!instockEl && instockText.indexOf(remoteLabel) === -1 && instockText.indexOf('fjernlager') === -1;

    // Tilgængeligt lager + ordre-leveringstid (kun sat på in-stock-grenen)
    var avail = null;
    var orderValue = '';
    if (countEl && countEl.hasAttribute('data-mk-avail')) {
      var availRaw = parseInt(countEl.getAttribute('data-mk-avail'), 10);
      if (!isNaN(availRaw)) avail = availRaw;
      orderValue = (countEl.getAttribute('data-mk-order-value') || '').trim();
    }

    var parsed = { property: property, value: value, show: show, inStock: inStock, avail: avail, orderValue: orderValue };
    log('  ↳ parsed:', parsed);
    return parsed;
  }

  function buildNewProperties(currentProps, deliveryInfo) {
    // Safety: hvis endpoint ikke gav valid shape, returner currentProps uændret
    if (!deliveryInfo || typeof deliveryInfo.show === 'undefined') {
      return currentProps || {};
    }

    var next = {};
    if (currentProps && typeof currentProps === 'object') {
      Object.keys(currentProps).forEach(function (k) {
        if (DELIVERY_PROPS.indexOf(k) === -1 && k !== IN_STOCK_PROP) {
          next[k] = currentProps[k];
        }
      });
    }

    if (deliveryInfo.show && deliveryInfo.property && deliveryInfo.value) {
      next[deliveryInfo.property] = deliveryInfo.value;
    }
    next[IN_STOCK_PROP] = deliveryInfo.inStock ? 'true' : 'false';

    return next;
  }

  function propsAreEqual(a, b) {
    var ak = Object.keys(a || {});
    var bk = Object.keys(b || {});
    if (ak.length !== bk.length) return false;
    for (var i = 0; i < ak.length; i++) {
      if (String(a[ak[i]]) !== String(b[ak[i]])) return false;
    }
    return true;
  }

  function changeLineItem(lineKey, newProperties) {
    log('POST /cart/change.js', { id: lineKey, properties: newProperties });
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, properties: newProperties })
    })
      .then(function (r) {
        log('  ↳ /cart/change.js status:', r.status);
        if (!r.ok) throw new Error('cart/change failed: ' + r.status);
        return r.json();
      })
      .then(function (result) {
        var hit = result.items && result.items.find(function (it) { return it.key === lineKey; });
        log('  ↳ updated line props:', hit ? hit.properties : '(not found in response)');
        return result;
      });
  }

  function syncItem(item) {
    if (!item || !item.handle || !item.variant_id) {
      warn('skip item, missing handle/variant_id:', item);
      return Promise.resolve(false);
    }

    group('item: ' + item.product_title + ' (' + item.handle + ')');
    log('current properties:', item.properties);
    log('variant_id:', item.variant_id, '| key:', item.key, '| qty:', item.quantity);

    return fetchDeliveryInfo(item.handle, item.variant_id)
      .then(function (info) {
        // Hvis kurv-antallet overstiger tilgængeligt lager, er varen reelt ikke
        // på lager → hop til ordre-leveringstid (næste step i logik-træet).
        if (info && info.inStock && info.avail != null && item.quantity > info.avail && info.orderValue) {
          log('  ↳ qty', item.quantity, '> avail', info.avail, '→ override til ordre-leveringstid');
          info.value = info.orderValue;
          info.inStock = false;
        }
        var nextProps = buildNewProperties(item.properties, info);
        log('proposed nextProps:', nextProps);
        var equal = propsAreEqual(item.properties || {}, nextProps);
        log('propsAreEqual:', equal);
        if (equal) {
          log('→ NO CHANGE');
          groupEnd();
          return false;
        }
        log('→ WILL UPDATE via /cart/change.js');
        return changeLineItem(item.key, nextProps).then(function () {
          groupEnd();
          return true;
        });
      })
      .catch(function (err) {
        warn('skip', item.handle, err.message);
        groupEnd();
        return false;
      });
  }

  function inspectThemeCart() {
    var theme = window.MinimogTheme && window.MinimogTheme.Cart;
    if (!theme) {
      warn('window.MinimogTheme.Cart not found — drawer DOM cannot be re-rendered');
      return null;
    }
    log('MinimogTheme.Cart present. Methods:', {
      refreshCart: typeof theme.refreshCart,
      renderNewCart: typeof theme.renderNewCart,
      updateCartCount: typeof theme.updateCartCount
    });
    return theme;
  }

  function refreshThemeCartUI(updatedCart) {
    group('refreshThemeCartUI');
    var theme = inspectThemeCart();
    return Promise.resolve()
      .then(function () {
        if (theme && typeof theme.refreshCart === 'function') {
          log('→ calling refreshCart()');
          return theme.refreshCart();
        }
      })
      .then(function () {
        if (theme && typeof theme.renderNewCart === 'function') {
          log('→ calling renderNewCart()');
          return theme.renderNewCart();
        }
      })
      .then(function () {
        var cartForUpdate = (theme && theme.cart) || updatedCart;
        if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
          log('→ calling Shopify.onCartUpdate()');
          window.Shopify.onCartUpdate(cartForUpdate, false);
        }
        if (theme && typeof theme.updateCartCount === 'function') {
          log('→ calling updateCartCount()');
          theme.updateCartCount(cartForUpdate);
        }
        log('refresh done');
        groupEnd();
      })
      .catch(function (e) {
        warn('refreshThemeCartUI failed', e);
        groupEnd();
      });
  }

  function run(triggerLabel) {
    if (inFlight) {
      log('run() requested by [' + (triggerLabel || 'manual') + '] but already in flight');
      return inFlight;
    }

    group('run() — trigger: ' + (triggerLabel || 'manual'));
    var startTime = performance.now ? performance.now() : Date.now();

    inFlight = fetchCart()
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
          log('cart is empty, nothing to sync');
          return false;
        }
        log('cart has', cart.items.length, 'items');
        var work = cart.items.map(function (item) {
          return syncItem(item);
        });
        return Promise.all(work).then(function (results) {
          var changedCount = results.filter(function (c) { return c; }).length;
          log('items changed:', changedCount, '/', results.length);
          return changedCount > 0;
        });
      })
      .then(function (anyChanged) {
        if (!anyChanged) {
          log('no changes — UI refresh skipped');
          return false;
        }
        return fetchCart().then(function (updated) {
          return refreshThemeCartUI(updated).then(function () {
            return true;
          });
        });
      })
      .catch(function (err) {
        warn('run() failed', err);
        return false;
      })
      .then(function (result) {
        var elapsed = (performance.now ? performance.now() : Date.now()) - startTime;
        log('run() done in', Math.round(elapsed), 'ms — anyChanged=', result);
        groupEnd();
        inFlight = null;
        return result;
      });

    return inFlight;
  }

  window.mkCartDeliveryResync = {
    run: function () { return run('manual'); },
    runAndWait: function () { return run('manual'); },
    get debug() { return debugMode; },
    set debug(v) { debugMode = !!v; },
    inspectThemeCart: inspectThemeCart
  };

  function attachCartDrawerTriggers() {
    var hooks = [];
    var cartUpdateDebounce = null;

    // Method 1: MinimogEvents (theme's own event bus — most reliable)
    function tryAttachEventBus() {
      if (window.MinimogEvents && typeof window.MinimogEvents.on === 'function') {
        window.MinimogEvents.on('ON_OPEN_DRAWER_CART', function () {
          log('event ON_OPEN_DRAWER_CART → triggering run()');
          run('event:ON_OPEN_DRAWER_CART');
        });
        window.MinimogEvents.on('ON_ITEM_ADDED', function () {
          log('event ON_ITEM_ADDED → triggering run()');
          run('event:ON_ITEM_ADDED');
        });
        // Antal-ændringer (+/- i åben drawer/cart) fyrer ON_CART_UPDATE.
        // Debounced så vi lader temaets egen opdatering sætte sig, og så resyncens
        // egen /cart/change.js ikke trigger en uendelig løkke (no-op ved næste run).
        window.MinimogEvents.on('ON_CART_UPDATE', function () {
          log('event ON_CART_UPDATE → (debounced) triggering run()');
          if (cartUpdateDebounce) clearTimeout(cartUpdateDebounce);
          cartUpdateDebounce = setTimeout(function () {
            run('event:ON_CART_UPDATE');
          }, 250);
        });
        hooks.push('MinimogEvents');
        return true;
      }
      return false;
    }
    if (!tryAttachEventBus()) {
      log('MinimogEvents not available yet — retrying in 500ms');
      setTimeout(function () {
        if (!tryAttachEventBus()) {
          warn('MinimogEvents still unavailable after retry');
        }
      }, 500);
    }

    // Method 2: MutationObserver on drawer wrapper as fallback
    var drawer = document.querySelector('[data-cart-drawer-backdrop], .scd__wrapper');
    if (drawer) {
      var lastHidden = drawer.classList.contains('hidden');
      log('drawer found. Initial hidden:', lastHidden, '| classes:', drawer.className);
      var observer = new MutationObserver(function (mutations) {
        var nowHidden = drawer.classList.contains('hidden');
        mutations.forEach(function (m) {
          log('drawer mutation:', m.attributeName, '| now hidden:', nowHidden);
        });
        if (lastHidden && !nowHidden) {
          log('drawer transitioned hidden→visible — triggering run()');
          run('mutation-observer');
        }
        lastHidden = nowHidden;
      });
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['class'],
        attributeOldValue: true
      });
      hooks.push('MutationObserver');
    } else {
      warn('cart drawer element (.scd__wrapper) NOT found at attach time');
    }

    // Method 3: click delegation on cart-icon links — last-resort safety net
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('[data-action="open-drawer-cart"], a[href="/cart"], a[href$="/cart"], [data-trigger-cart]');
      if (!trigger) return;
      log('cart-icon click detected on', trigger.tagName, trigger.className, '→ triggering run()');
      run('cart-icon-click');
    }, true);
    hooks.push('click-delegation');

    log('drawer triggers attached:', hooks.join(', '));
  }

  function attachCheckoutInterceptor() {
    document.addEventListener(
      'click',
      function (e) {
        var btn = e.target.closest && e.target.closest('.scd__checkout, .cart__submit');
        if (!btn) return;
        if (btn.dataset.mkResyncDone === 'true') {
          log('checkout click after resync — letting it through');
          return;
        }

        log('checkout click intercepted — running resync first');
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        btn.classList.add('is-loading');
        run('checkout-click')
          .catch(function (err) {
            warn('checkout resync failed', err);
          })
          .then(function () {
            btn.dataset.mkResyncDone = 'true';
            btn.classList.remove('is-loading');
            log('re-clicking checkout button now');
            btn.click();
          });
      },
      true
    );
  }

  function init() {
    log('=== mk-cart-delivery-resync.js initialized ===');
    log('debug mode:', debugMode, '| toggle via: window.mkCartDeliveryResync.debug = true');
    inspectThemeCart();
    attachCheckoutInterceptor();
    attachCartDrawerTriggers();

    var path = window.location && window.location.pathname;
    if (path === '/cart' || path === '/cart/') {
      log('on /cart page → triggering initial run()');
      run('cart-page-load');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
